import axios from 'axios';
import { getCurrentToken, clearUserSession } from '../utils/userStorage';
import { getApiBaseUrl } from '../config/apiConfig';

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = getCurrentToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const code = error.response?.data?.code;
    const isExpiredSession = status === 401 || (
      status === 403 && ['TOKEN_EXPIRED', 'INVALID_TOKEN', 'NO_TOKEN', 'USER_NOT_FOUND'].includes(String(code || ''))
    );

    if (isExpiredSession && error.response?.data) {
      console.log('🔒 Sesión inválida o expirada, limpiando sesión...');
      if (window.location.pathname === '/cart') {
        localStorage.setItem('peekpass-pending-auth-intent', JSON.stringify({
          type: 'cart-checkout',
          returnTo: '/cart?checkout=1',
          createdAt: new Date().toISOString()
        }));
      }
      clearUserSession();
      if (window.location.pathname !== '/') {
        window.location.href = '/?auth=login';
      }
    }
    // Para errores de red o timeout, no limpiar la sesión
    return Promise.reject(error);
  }
);

export interface UserData {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  timezone: string;
  role: 'client' | 'organizer' | 'admin';
  isVerified: boolean;
  isFirstLogin: boolean;
  interests?: string[];
  profession?: string;
  documentType?: string;
  documentNumber?: string;
  paymentMethods?: {
    cbu: string;
    mercadoPago: string;
    lemon: string;
    stripe: boolean;
  };
  createdAt: string;
}

export interface ProducerProfile {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'client' | 'organizer' | 'admin';
  publicProfileName: string;
  producerBio: string;
  profilePicture?: string;
  website?: string;
  instagram?: string;
  updatedAt?: string;
}

export interface ProducerProfileUpdate {
  firstName: string;
  lastName: string;
  email: string;
  publicProfileName: string;
  producerBio: string;
  profilePictureFile?: File | null;
  removeProfilePicture?: boolean;
  website?: string;
  instagram?: string;
}

export interface DashboardNotification {
  id: string;
  type: 'welcome' | 'mercadopago' | 'ticket' | 'sale' | 'system';
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  metadata?: Record<string, unknown>;
}

export interface TicketData {
  id: string;
  ticketId: string;
  orderId?: string;
  paymentId?: string;
  eventId: string;
  eventTitle: string;
  eventDate?: string;
  eventTime?: string;
  eventAddress?: string;
  ticketTierId?: string;
  ticketTierName?: string;
  buyerName?: string;
  buyerEmail?: string;
  organizerName?: string;
  qrUrl?: string;
  qrImageUrl?: string;
  status: 'active' | 'used' | 'cancelled' | 'refunded' | 'approved_stock_conflict' | 'manual_review';
  reviewMessage?: string;
  usedAt?: string | null;
  priceBreakdown?: {
    entryValue?: number;
    serviceFeeRate?: number;
    serviceFeeAmount?: number;
    totalPaid?: number;
    organizerAmount?: number;
    peekpassAmount?: number;
  };
  createdAt?: string;
}

export interface ClassData {
  _id?: string;
  title: string;
  description: string;
  eventForms?: string;
  serviceFeeRate?: number;
  price: number;
  currency: string;
  duration: string;
  difficulty: 'Principiante' | 'Intermedio' | 'Avanzado';
  category: string;
  organizer: {
    id: string;
    name: string;
    avatar: string;
    rating: number;
    clients: number;
    country: string;
    producerBio?: string;
    website?: string;
    instagram?: string;
    mercadoPagoLinked?: boolean;
    canReceivePayments?: boolean;
  };
  reelUrl: string;
  thumbnail: string;
  exercises: string[];
  benefits: string[];
  requirements: string[];
  isPopular: boolean;
  discount?: number;
  eventDate?: string;
  venue?: {
    name?: string;
    address?: string;
    city?: string;
  };
  ticketType?: string;
  ticketCapacity?: number;
  ticketTiers?: Array<{
    id: string;
    name: string;
    quantity: number;
    price: number;
    availableFrom?: string;
    availableUntil?: string;
  }>;
  ageRestriction?: string;
  likes?: string[];
  isActive: boolean;
  isFallback?: boolean;
  isShortVideo?: boolean; // Agregado para reels de 45 segundos
  createdAt: string;
  updatedAt: string;
}

export interface ClassesResponse {
  success: boolean;
  data: ClassData[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    user: UserData;
    token: string;
  };
}

export interface RegisterResponse {
  success: boolean;
  message: string;
  data?: {
    user: UserData;
    token: string;
  };
}

export const getClasses = async (params?: {
  category?: string;
  difficulty?: string;
  search?: string;
  sortBy?: string;
  limit?: number;
  page?: number;
}): Promise<ClassesResponse> => {
  try {
    const response = await api.get('/classes', { params });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Error obteniendo eventos');
  }
};

export const getClassById = async (id: string): Promise<ClassData> => {
  try {
    const response = await api.get(`/classes/${id}`);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Error obteniendo evento');
  }
};

export const getUserProfile = async (): Promise<{ user: UserData }> => {
  try {
    const response = await api.get('/auth/profile');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Error obteniendo perfil');
  }
};

export const loginUser = async (email: string, password: string, recaptchaToken?: string): Promise<any> => {
  try {
    console.log('🔐 Intentando login para:', email);
    console.log('🔗 URL:', `${API_BASE_URL}/auth/login`);
    const response = await api.post('/auth/login', { email, password, recaptchaToken });
    console.log('✅ Login exitoso:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('❌ Error en login:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || 'Error en el login');
  }
};

export const registerUser = async (userData: any): Promise<any> => {
  try {
    const response = await api.post('/auth/register', userData);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Error en el registro');
  }
};

export const healthCheck = async (): Promise<any> => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error: any) {
    return {
      status: 'ERROR',
      timestamp: new Date().toISOString(),
      service: 'PEEK PASS API Server',
      database: 'Disconnected',
      error: error.message
    };
  }
};

export const getProducerProfile = async (): Promise<ProducerProfile> => {
  try {
    const response = await api.get('/auth/profile');
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'No pudimos cargar tu perfil');
  }
};

export const updateProducerProfile = async (updates: ProducerProfileUpdate): Promise<ProducerProfile> => {
  try {
    const formData = new FormData();
    formData.append('firstName', updates.firstName);
    formData.append('lastName', updates.lastName);
    formData.append('email', updates.email);
    formData.append('publicProfileName', updates.publicProfileName);
    formData.append('producerBio', updates.producerBio);
    formData.append('website', updates.website || '');
    formData.append('instagram', updates.instagram || '');
    formData.append('removeProfilePicture', String(Boolean(updates.removeProfilePicture)));
    if (updates.profilePictureFile) formData.append('profilePicture', updates.profilePictureFile);

    const response = await api.put('/auth/profile', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'No pudimos guardar los cambios');
  }
};

export const changeProducerPassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  try {
    await api.put('/auth/profile/password', { currentPassword, newPassword });
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'No pudimos actualizar la contraseña');
  }
};

export const getDashboardNotifications = async (): Promise<DashboardNotification[]> => {
  try {
    const response = await api.get('/notifications');
    return Array.isArray(response.data?.data) ? response.data.data : [];
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'No pudimos cargar las notificaciones');
  }
};

export const markDashboardNotificationsRead = async (ids?: string[]): Promise<DashboardNotification[]> => {
  try {
    const response = await api.patch('/notifications/read', ids?.length ? { ids } : {});
    return Array.isArray(response.data?.data) ? response.data.data : [];
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'No pudimos actualizar las notificaciones');
  }
};

export const getMyTickets = async (): Promise<TicketData[]> => {
  try {
    const response = await api.get('/tickets/my');
    return Array.isArray(response.data?.data) ? response.data.data : [];
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'No pudimos cargar tus entradas');
  }
};

export const getOrganizerSales = async (): Promise<TicketData[]> => {
  try {
    const response = await api.get('/tickets/organizer/sales');
    return Array.isArray(response.data?.data) ? response.data.data : [];
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'No pudimos cargar las ventas');
  }
};

export const getTicketById = async (ticketId: string): Promise<TicketData> => {
  try {
    const response = await api.get(`/tickets/${encodeURIComponent(ticketId)}`);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'No pudimos cargar la entrada');
  }
};

export const markTicketUsed = async (ticketId: string): Promise<TicketData> => {
  try {
    const response = await api.post(`/tickets/${encodeURIComponent(ticketId)}/use`);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'No pudimos validar la entrada');
  }
};

export const deleteMyTicket = async (ticketId: string): Promise<TicketData> => {
  try {
    const response = await api.delete(`/tickets/${encodeURIComponent(ticketId)}/my`);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'No pudimos eliminar la entrada');
  }
};

export const getFullImageUrl = (relativeUrl: string | undefined | null): string => {
  if (!relativeUrl) return '';

  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
    return relativeUrl;
  }

  if (relativeUrl.startsWith('/')) {
    return `${API_BASE_URL}${relativeUrl}`;
  }

  return `${API_BASE_URL}/${relativeUrl}`;
};

export const toggleClassLike = async (id: string): Promise<{ likes: string[]; likeCount: number; isLiked: boolean }> => {
  try {
    const response = await api.post(`/classes/${id}/likes`);
    return response.data.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'No pudimos actualizar el like');
  }
};

export const getAllUsers = async (): Promise<any> => {
  try {
    const response = await api.get('/users');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Error obteniendo usuarios');
  }
};

export const updateUserProfile = async (updates: any): Promise<any> => {
  try {
    const response = await api.put('/auth/profile', updates);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Error actualizando perfil');
  }
};

export const markFirstLoginCompleted = async (): Promise<any> => {
  try {
    const response = await api.put('/auth/first-login-completed');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Error actualizando primer login');
  }
};

export const updateClientInterests = async (interests: string[]): Promise<any> => {
  try {
    const response = await api.put('/auth/interests', { interests });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Error actualizando intereses');
  }
};

export const deleteUser = async (userId: string): Promise<any> => {
  try {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Error eliminando usuario');
  }
};

// ==================== 💰 MERCADOPAGO MARKETPLACE ====================

/**
 * Verificar el estado de vinculación de MercadoPago del organizador
 */
export const checkMercadoPagoStatus = async (): Promise<any> => {
  try {
    const response = await api.get('/auth/mercadopago/status');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Error verificando estado de MercadoPago');
  }
};

/**
 * Iniciar el flujo de vinculación con MercadoPago
 * Retorna la URL de conexión para redirección manual
 */
export const connectMercadoPago = async (): Promise<{ connectionUrl: string }> => {
  const baseUrl = getApiBaseUrl();
  const connectionUrl = `${baseUrl}/auth/mercadopago/connect`;
  return { connectionUrl };
};

/**
 * Guardar credenciales de MercadoPago del organizador (APP_ID y APP_SECRET)
 */
export const saveMercadoPagoCredentials = async (appId: string, appSecret: string): Promise<any> => {
  try {
    const response = await api.post('/auth/mercadopago/credentials', {
      appId,
      appSecret
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Error guardando credenciales de MercadoPago');
  }
};

/**
 * Desvincular cuenta de MercadoPago
 */
export const disconnectMercadoPago = async (): Promise<any> => {
  try {
    const response = await api.post('/auth/mercadopago/disconnect');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Error desvinculando MercadoPago');
  }
};

export default api;
